import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

function readAdminToken(): string | null {
  const direct = localStorage.getItem('admin_token');
  if (direct) return direct;
  try {
    const raw = localStorage.getItem('astrovrinda-admin-auth');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.state?.token || null;
  } catch {
    return null;
  }
}

api.interceptors.request.use((config) => {
  const token = readAdminToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ─── Auth ─────────────────────────────────────────────────────────────────────
export const adminLogin = (email: string, password: string) =>
  api.post('/admin/auth/login', { email, password });
export const getAdminMe = () => api.get('/admin/auth/me');

// ─── Dashboard ────────────────────────────────────────────────────────────────
export const getDashboard = () => api.get('/admin/dashboard');
export const getConsultationAnalytics = (days = 30) =>
  api.get(`/admin/analytics/consultations?days=${days}`);
export const getRevenueAnalytics = (days = 30) =>
  api.get(`/admin/analytics/revenue?days=${days}`);

// ─── Users ────────────────────────────────────────────────────────────────────
export const getUsers = (params?: any) => api.get('/admin/users', { params });
export const getUser = (id: string) => api.get(`/admin/users/${id}`);
export const updateUser = (id: string, data: any) => api.put(`/admin/users/${id}`, data);
export const deleteUser = (id: string) => api.delete(`/admin/users/${id}`);
export const creditUserWallet = (id: string, amount: number, reason: string) =>
  api.post(`/admin/users/${id}/wallet/credit`, { amount, reason });
export const getUserActivity = (id: string, params?: any) =>
  api.get(`/admin/users/${id}/activity`, { params });

// ─── Astrologers ──────────────────────────────────────────────────────────────
export const getAstrologers = (params?: any) => api.get('/admin/astrologers', { params });
export const getAstrologer = (id: string) => api.get(`/admin/astrologers/${id}`);
export const getAstrologerActivity = (id: string, params?: any) =>
  api.get(`/admin/astrologers/${id}/activity`, { params });
export const createAstrologer = (data: any) => api.post('/admin/astrologers', data);
export const updateAstrologer = (id: string, data: any) => api.put(`/admin/astrologers/${id}`, data);
export const updateAstrologerApproval = (id: string, data: any) =>
  api.put(`/admin/astrologers/${id}/approval`, data);
export const deleteAstrologer = (id: string) => api.delete(`/admin/astrologers/${id}`);
export const setAstrologerCredentials = (id: string, data: { loginId?: string; password?: string }) =>
  api.put(`/admin/astrologers/${id}/credentials`, data);
export const getFeatureSettings = () => api.get('/admin/features');
export const updateFeatureSettings = (features: any) => api.put('/admin/features', { features });

// ─── Astrologer Applications ──────────────────────────────────────────────────
export const getAstroApplications = (params?: any) => api.get('/admin/astro-applications', { params });
export const getAstroApplication = (id: string) => api.get(`/admin/astro-applications/${id}`);
export const approveAstroApplication = (id: string, data?: any) =>
  api.put(`/admin/astro-applications/${id}/approve`, data || {});
export const rejectAstroApplication = (id: string, data: { reason: string }) =>
  api.put(`/admin/astro-applications/${id}/reject`, data);
export const updateAstroApplicationKyc = (id: string, data: { status: string; note?: string }) =>
  api.put(`/admin/astro-applications/${id}/kyc`, data);
export const updateAstrologerKyc = (id: string, data: { status: string; note?: string }) =>
  api.put(`/admin/astrologers/${id}/kyc`, data);
export const getAstrologerSchedule = (id: string) =>
  api.get(`/admin/astrologers/${id}/schedule`);

/** Upload image for admin (astrologer profile, banners, etc.) */
export const uploadAdminImage = (file: File) => {
  const form = new FormData();
  form.append('image', file);
  return api.post('/admin/upload/image', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    transformRequest: [(data, headers) => {
      if (headers && typeof headers === 'object') {
        delete (headers as any)['Content-Type'];
      }
      return data;
    }],
  });
};

/** Upload wide promo banner artwork */
export const uploadBannerImage = (file: File) => {
  const form = new FormData();
  form.append('image', file);
  return api.post('/admin/upload/banner', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    transformRequest: [(data, headers) => {
      if (headers && typeof headers === 'object') {
        delete (headers as any)['Content-Type'];
      }
      return data;
    }],
  });
};

/** Resolve relative /uploads/... paths to absolute API host */
export const resolveMediaUrl = (url?: string | null) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  const base = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
  return `${base}${url.startsWith('/') ? url : `/${url}`}`;
};

// ─── Categories ───────────────────────────────────────────────────────────────
export const getCategories = () => api.get('/admin/categories');
export const createCategory = (data: any) => api.post('/admin/categories', data);
export const updateCategory = (id: string, data: any) => api.put(`/admin/categories/${id}`, data);
export const deleteCategory = (id: string) => api.delete(`/admin/categories/${id}`);

// ─── Languages ────────────────────────────────────────────────────────────────
export const getLanguages = () => api.get('/admin/languages');
export const createLanguage = (data: any) => api.post('/admin/languages', data);
export const updateLanguage = (id: string, data: any) => api.put(`/admin/languages/${id}`, data);

// ─── Translations ─────────────────────────────────────────────────────────────
export const getTranslations = (params?: any) => api.get('/admin/translations', { params });
export const upsertTranslation = (data: any) => api.post('/admin/translations', data);
export const deleteTranslation = (id: string) => api.delete(`/admin/translations/${id}`);

// ─── Banners ──────────────────────────────────────────────────────────────────
export const getBanners = () => api.get('/admin/banners');
export const createBanner = (data: any) => api.post('/admin/banners', data);
export const updateBanner = (id: string, data: any) => api.put(`/admin/banners/${id}`, data);
export const deleteBanner = (id: string) => api.delete(`/admin/banners/${id}`);

// ─── Offers / Coupons ─────────────────────────────────────────────────────────
export const getOffers = () => api.get('/admin/offers');
export const createOffer = (data: any) => api.post('/admin/offers', data);
export const updateOffer = (id: string, data: any) => api.put(`/admin/offers/${id}`, data);
export const deleteOffer = (id: string) => api.delete(`/admin/offers/${id}`);

// ─── FAQs ─────────────────────────────────────────────────────────────────────
export const getFAQs = () => api.get('/admin/faqs');
export const createFAQ = (data: any) => api.post('/admin/faqs', data);
export const updateFAQ = (id: string, data: any) => api.put(`/admin/faqs/${id}`, data);
export const deleteFAQ = (id: string) => api.delete(`/admin/faqs/${id}`);

// ─── Settings ─────────────────────────────────────────────────────────────────
export const getSettings = () => api.get('/admin/settings');
export const upsertSetting = (data: any) => api.post('/admin/settings', data);

// ─── Consultations ────────────────────────────────────────────────────────────
export const getAdminConsultations = (params?: any) => api.get('/admin/consultations', { params });
export const getAdminConsultation = (id: string) => api.get(`/admin/consultations/${id}`);
export const getAdminConsultationMessages = (id: string, params?: any) =>
  api.get(`/admin/consultations/${id}/messages`, { params });

// ─── Support ──────────────────────────────────────────────────────────────────
export const getAdminSupportTickets = (params?: any) => api.get('/admin/support', { params });
export const getAdminSupportTicket = (id: string) => api.get(`/admin/support/${id}`);
export const replyAdminSupportTicket = (id: string, data: { message: string; resolve?: boolean }) =>
  api.post(`/admin/support/${id}/reply`, data);
export const updateAdminSupportTicket = (id: string, data: Record<string, unknown>) =>
  api.put(`/admin/support/${id}`, data);

// ─── Finance ──────────────────────────────────────────────────────────────────
export const getSystemRevenue = (params?: any) => api.get('/admin/revenue', { params });
export const getAdminTransactions = (params?: any) => api.get('/admin/transactions', { params });
export const getAdminTransaction = (id: string) => api.get(`/admin/transactions/${id}`);
export const getAdminPayments = (params?: any) => api.get('/admin/payments', { params });
export const getAdminPayment = (id: string) => api.get(`/admin/payments/${id}`);

export const getPaymentGateways = () => api.get('/admin/payment-gateways');
export const updatePaymentGateway = (code: string, data: any) =>
  api.put(`/admin/payment-gateways/${code}`, data);
export const activatePaymentGateway = (code: string) =>
  api.post(`/admin/payment-gateways/${code}/activate`);
export const uploadManualQrImage = (file: File) => {
  const fd = new FormData();
  fd.append('image', file);
  return api.post('/admin/payment-gateways/manual/qr', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getPaymentFinance = () => api.get('/admin/payment-finance');
export const updatePaymentFinance = (data: any) => api.put('/admin/payment-finance', data);
export const getRechargePackagesAdmin = () => api.get('/admin/recharge-packages');
export const updateRechargePackagesAdmin = (data: any) => api.put('/admin/recharge-packages', data);
export const getPaymentReviews = (params?: any) => api.get('/admin/payment-reviews', { params });
export const reviewManualPayment = (id: string, data: { decision: 'approved' | 'rejected'; note?: string }) =>
  api.post(`/admin/payment-reviews/${id}`, data);
export const getPaymentStats = (params?: any) => api.get('/admin/payment-stats', { params });
export const getGatewayTransactions = (params?: any) =>
  api.get('/admin/gateway-transactions', { params });

// ─── Integrations ─────────────────────────────────────────────────────────────
export const getIntegrations = () => api.get('/admin/integrations');
export const updateIntegration = (code: string, data: any) =>
  api.put(`/admin/integrations/${code}`, data);
export const activateIntegration = (code: string) =>
  api.post(`/admin/integrations/${code}/activate`);
export const deactivateIntegration = (code: string) =>
  api.post(`/admin/integrations/${code}/deactivate`);

export const getAdminNotifications = (params?: any) => api.get('/admin/notifications', { params });
export const sendAdminNotification = (data: any) => api.post('/admin/notifications', data);

// ─── Commission (PDF §27) ─────────────────────────────────────────────────────
export const getCommission = () => api.get('/admin/commission');
export const updateCommission = (data: any) => api.put('/admin/commission', data);

// ─── CMS (PDF §34) ────────────────────────────────────────────────────────────
export const getCmsPages = (params?: any) => api.get('/admin/cms', { params });
export const getCmsPage = (slug: string) => api.get(`/admin/cms/${slug}`);
export const upsertCmsPage = (slug: string, data: any) => api.put(`/admin/cms/${slug}`, data);
export const deleteCmsPage = (slug: string) => api.delete(`/admin/cms/${slug}`);

// ─── Withdrawals (PDF §29) ────────────────────────────────────────────────────
export const getAdminWithdrawals = (params?: any) => api.get('/admin/withdrawals', { params });
export const approveWithdrawal = (id: string, data?: { adminNote?: string }) =>
  api.put(`/admin/withdrawals/${id}/approve`, data || {});
export const rejectWithdrawal = (id: string, data: { adminNote?: string }) =>
  api.put(`/admin/withdrawals/${id}/reject`, data);
export const settleWithdrawal = (id: string, data: { settlementReference: string; adminNote?: string }) =>
  api.put(`/admin/withdrawals/${id}/settle`, data);

// ─── Appointments (PDF §30) ───────────────────────────────────────────────────
export const getAdminAppointments = (params?: any) => api.get('/admin/appointments', { params });
export const getAdminAppointment = (id: string) => api.get(`/admin/appointments/${id}`);

// ─── Emergency settings (PDF §43.1) ───────────────────────────────────────────
export const getEmergencySettings = () => api.get('/admin/emergency-settings');
export const updateEmergencySettings = (data: {
  enabled?: boolean;
  surchargePaise?: number;
  label?: string;
}) => api.put('/admin/emergency-settings', data);

// ─── Reviews (PDF §14) ────────────────────────────────────────────────────────
export const getAdminReviews = (params?: any) => api.get('/admin/reviews', { params });
export const moderateReview = (id: string, data: { action: 'approve' | 'reject'; adminNote?: string }) =>
  api.put(`/admin/reviews/${id}/moderate`, data);

// ─── Activity logs (PDF §36) ──────────────────────────────────────────────────
export const getActivityLogs = (params?: any) => api.get('/admin/activity-logs', { params });

// ─── Reports (PDF §35) ────────────────────────────────────────────────────────
export const getReport = (kind: string, params?: any) =>
  api.get(`/admin/reports/${kind}`, { params });

export default api;
